import { ValidationError } from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Items } from '@prisma/client';
import useUser from '../global/useUser';
import { itemForm, ItemForm } from '../../types/items';
import { updateItem, uploadItemImages } from '../../services/items';
import { ImageItem } from '../../utils/form/appointmentForm';

type Payload = {
  itemId: string;
  payload: ItemForm;
};

interface Options {
  onSuccess?: (item: Items, variables: Payload) => void | Promise<void>;
  onError?: (message: string) => void | Promise<void>;
  onMutate?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
}

function useUpdateItem({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({ itemId, payload }: Payload) => {
      if (!user?.business?.id) {
        throw new Error('No business ID found');
      }

      const validated = await itemForm.noUnknown().validate(
        { itemId, ...payload },
        {
          stripUnknown: true,
        },
      );

      const formImages = (validated.images ?? []) as ImageItem[];
      const newImages = formImages.filter(i => !i.isUploaded);
      const existingUrls = formImages.filter(i => i.isUploaded).map(i => i.url);
      const uploadedUrls = newImages.length
        ? await uploadItemImages(newImages, user.business.id)
        : [];

      return updateItem({
        itemId,
        businessId: user.business.id,
        payload: {
          ...validated,
          imageUrls: [...existingUrls, ...uploadedUrls],
        },
      });
    },
    onSuccess,
    onError: (error: Error) => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
        return;
      }
      onError?.('Failed to update item');
    },
    onMutate,
    onSettled,
  });
}

export default useUpdateItem;
